import { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { CIRCLE_IMAGE_OF_CHARACTER } from 'src/constants/characters'
import { useAuthState } from 'src/providers/AuthProviders'
import getUnreadNews from 'src/services/get/getUnreadNews'
import useLoading from 'src/components/layouts/hooks/useLoading'
import getCurrentEvents from 'src/services/get/getCurrentEvents'
import { useGetRecommendsData } from 'src/services/hooks/useFetchRecommends'
import { getRankingD6 } from 'src/services/ranking'
import getUserPresents from 'src/services/get/getUserPresents'
import CancelableAPIRequest from 'src/models/CancelableAPIRequest'
import { postUserLivestream } from 'src/services/post/postUserLivestream'
import { getListLivestreams } from 'src/services/get/getListLivestreams'
import { setFirstLive, setSecondLive } from 'src/redux/live/setLiveList'

import StartLoading from 'src/components/layouts/StartLoading'
import TipsModal from 'src/components/common/TipsModal'
import Loading from 'src/components/layouts/Loading'
import { MainLayout } from 'src/components/layouts/MainLayout'
import EventSlideBanner from 'src/components/myPage/EventSlideBanner'
import RecommendedMan from 'src/components/myPage/RecommendedMan'
import TopRanking from 'src/components/myPage/TopRanking'
import { NotificationArea } from 'src/components/myPage/NotificationArea'

import { HistoryRouteContext } from 'src/routes'
import { VOICE_LIVE_PATH } from 'src/routes/live'

import Booth from './livestream/Booth'
import { ModalExtraTime } from '../common/ModalExtraTime'
import LiveBooth from './livestream/LiveBooth'
import Schedule from './livestream/Schedule'
import { queryClient } from 'src/providers/QueryProvider'

const MyPageContainer = () => {
  const { user } = useAuthState()
  const scheduleRef = useRef(null)
  const [haveScheduleToday, sethaveScheduleToday] = useState(false)
  const { redirectTo } = useContext(HistoryRouteContext)
  const recommends = useGetRecommendsData()
  const [presents, setPresents] = useState([])
  const hasUnreadNews = useQuery('has-unread-news', () => getUnreadNews(user.id))
  const events = useQuery('events/mypage', () => getCurrentEvents(), {
    cacheTime: 0,
    select: (data) =>
      data.filter((event) => !event.displayPeriodSetting && event.displayOnTheMyPage)
  })
  const topRankingCharacters = useQuery('top-ranking-week', () => getRankingD6('current_week'))
  const dispatch = useDispatch()
  const { data: liveSettings, refetch } = useQuery({
    queryFn: async () => {
      const res = await getListLivestreams()
      if (res) {
        let allLivestreams = res.flatMap((item) => item.livestreams || [])
        const liveEndIds = queryClient.getQueryData('live_end_ids')
        if (liveEndIds && liveEndIds.length > 0) {
          allLivestreams = allLivestreams.filter((live) => !liveEndIds.includes(live.id))
        }

        const firstLive = allLivestreams.find((live) => live.live_type === 'Aブース')
        const secondLive = allLivestreams.find((live) => live.live_type === 'Bブース')
        dispatch(setFirstLive(firstLive))
        dispatch(setSecondLive(secondLive))

        return {
          data: res,
          firstSetting: res.find((item) =>
            item.livestreams?.some((live) => live.id === firstLive?.id)
          ),
          secondSetting: res.find((item) =>
            item.livestreams?.some((live) => live.id === secondLive?.id)
          )
        }
      }
    },
    queryKey: 'get-live-setting-infos'
  })
  const liveList = useSelector((state) => state.liveList)
  const [openExtralModal, setOpenExtraModal] = useState(false)

  useEffect(() => {
    const req = new CancelableAPIRequest(getUserPresents, user?.id)
    req.invoke((resp) => {
      setPresents(resp)
    })

    return () => req.cancel()
  }, [])

  const findRecommendMan = useMemo(
    () => recommends.data?.find((man) => man.recommend),
    [recommends.data]
  )

  const recommendManImage = useMemo(() => {
    if (!findRecommendMan) return
    return CIRCLE_IMAGE_OF_CHARACTER[findRecommendMan.id]
  }, [findRecommendMan])

  const createUserLivestream = async (livestreamSettingId) => {
    await postUserLivestream(livestreamSettingId)
  }

  useEffect(() => {
    localStorage.removeItem('idTokenRegister')
    localStorage.removeItem('accessTokenRegister')
    refetch()
  }, [])

  useEffect(() => {
    if (liveSettings) {
      liveSettings.data.forEach((liveSetting) => createUserLivestream(liveSetting.id))

      const liveEndIds = queryClient.getQueryData('live_end_ids') || []
      if (liveEndIds.length > 0) {
        const allLivestreams = liveSettings.data.flatMap((item) => item.livestreams || [])
        const updatedLiveEndIds = liveEndIds.filter(
          (id) => !allLivestreams.some((live) => live.id === id && live.status === 'completion')
        )
        queryClient.setQueryData('live_end_ids', updatedLiveEndIds)

        if (updatedLiveEndIds.length > 0) {
          refetch()
        }
      }
    }
  }, [liveSettings])

  const handleScrollToSchedule = () => {
    if (haveScheduleToday) {
      scheduleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      redirectTo(VOICE_LIVE_PATH.LiveSchedulePage, { state: { tab: 'current_month' } })
    }
  }

  const [isAfterOpening] = useState(localStorage.getItem('isStoryLoading'))
  const [isReady, setIsReady] = useState(false)
  const startTime = useRef(Date.now())
  const { showLoading, progress } = useLoading([!isReady])

  useEffect(() => {
    if (isAfterOpening) {
      if (hasUnreadNews.data !== null && events.data && recommends.data && recommendManImage) {
        const elapsed = Date.now() - startTime.current
        const delay = Math.max(1300 - elapsed, 0)
        const timer = setTimeout(() => {
          setIsReady(true)
        }, delay)
        return () => clearTimeout(timer)
      } else {
        setIsReady(false)
      }
    } else {
      if (hasUnreadNews.data !== null && events.data && recommends.data && recommendManImage) {
        setIsReady(true)
      } else {
        setIsReady(false)
      }
    }
  }, [hasUnreadNews, events, recommends, recommendManImage])

  if (!isReady) {
    return (
      <LoadingWrapper>
        {isAfterOpening ? <StartLoading progress={progress} /> : <Loading progress={progress} />}
      </LoadingWrapper>
    )
  }

  return (
    <MainLayout
      className="relative"
      classNameHeader=""
      showFooter={!showLoading}
      showHeader={!showLoading}
    >
      <Wrapper>
        <EventSlideBanner events={events.data} />
        <div className="grid grid-cols-2 mt-12 font-bold justify-items-center gap-1 px-2">
          {liveSettings && (liveList?.firstLive || liveList?.secondLive) ? (
            <>
              <LiveBooth
                setOpenExtraModal={setOpenExtraModal}
                handleScrollToSchedule={handleScrollToSchedule}
                livestream={liveList.firstLive}
                refetch={refetch}
                user={user}
                liveSetting={liveSettings.firstSetting}
                title="A"
                isFirst={true}
              />
              <LiveBooth
                setOpenExtraModal={setOpenExtraModal}
                handleScrollToSchedule={handleScrollToSchedule}
                livestream={liveList.secondLive}
                refetch={refetch}
                user={user}
                liveSetting={liveSettings.secondSetting}
                title="B"
                isFirst={false}
              />
            </>
          ) : (
            <>
              <Booth title="A" handleScrollToSchedule={handleScrollToSchedule} />
              <Booth title="B" handleScrollToSchedule={handleScrollToSchedule} />
            </>
          )}
        </div>
        <ModalExtraTime isOpen={openExtralModal} onClose={() => setOpenExtraModal(false)} />
        <TopRanking
          className="mt-5"
          characters={Object.fromEntries(
            Object.entries(topRankingCharacters?.data?.rank || {}).slice(0, 3)
          )}
        />
        <RecommendedMan infor={{ ...findRecommendMan, imageSrc: recommendManImage }} />
        <div ref={scheduleRef}>
          <Schedule sethaveScheduleToday={sethaveScheduleToday} />
        </div>
      </Wrapper>
      <NotificationArea presents={presents} />
      <TipsModal type="mypage" />
    </MainLayout>
  )
}

export default MyPageContainer

const Wrapper = styled.div.attrs({
  className: 'relative pt-12 pb-4 overflow-y-scroll hidden-scroll bg-white'
})`
  height: calc(100dvh - 74px);
`
const LoadingWrapper = styled.div.attrs({
  className: 'relative pt-12 pb-4 overflow-y-scroll hidden-scroll bg-white'
})`
  height: calc(100dvh);
`
